import { useMemo, useState } from "react";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer";
import { Link } from "react-router-dom";

import DiffToolbar, { DiffViewMode } from "./DiffToolbar";
import useDiffVersions from "./hooks/useDiffVersions";
import MessageDiff from "./MessageDiff";
import MetadataDiff from "./MetadataDiff";

import LoadingSpinner from "@/components/LoadingSpinner";
import NotFoundMessage from "@/components/ui/not-found";
import BreadcrumbsPageWrapper from "@/components/UnifiedRegistry/BreadcrumbsPageWrapper";
import useFolderPathBreadcrumbs from "@/components/UnifiedRegistry/hooks/useFolderPathBreadcrumbs";
import { useUser } from "@/context/user-context";
import { PromptVersion } from "@/types/apiGetters";

const formatPromptVersionForJsonDiff = (data?: PromptVersion) =>
  JSON.stringify({ ...data?.metadata?.model, ...data?.prompt_template }, null, 2).replace(
    /\\n/g,
    "\n",
  );

const DiffPromptTemplate = () => {
  const userContext = useUser();
  const [viewMode, setViewMode] = useState<DiffViewMode>("readable");

  const {
    promptId,
    versionNumber,
    diffVersionNumber,
    promptRegistry,
    sourceVersion,
    targetVersion,
    sourceTemplate,
    targetTemplate,
    isLoading,
    isXrayMode,
    setIsXrayMode,
    isXrayAvailable,
  } = useDiffVersions();

  const folderBreadcrumbs = useFolderPathBreadcrumbs(
    promptRegistry?.folder_id ?? 0,
    `${promptRegistry?.prompt_name} [Diff view]`,
  );

  const breadcrumbProps = useMemo(() => {
    if (!promptRegistry) {
      return folderBreadcrumbs;
    }

    const itemsWithPrompt = [
      ...folderBreadcrumbs.items.slice(0, -1),
      {
        id: promptRegistry.id,
        label: promptRegistry.prompt_name,
        href: `/workspace/${userContext?.activeWorkspaceId}/prompt/${promptId}`,
      },
      folderBreadcrumbs.items[folderBreadcrumbs.items.length - 1],
    ];

    return {
      ...folderBreadcrumbs,
      items: itemsWithPrompt,
    };
  }, [folderBreadcrumbs, promptRegistry, userContext?.activeWorkspaceId, promptId]);

  if (!promptId || !versionNumber || !diffVersionNumber) {
    return (
      <NotFoundMessage
        title="Template not found"
        subtitle="We couldn't find the prompt template you were looking for."
        backLink="/prompt"
        backLinkMessage="Back to Prompt Registry"
      />
    );
  }

  if (isLoading) {
    return (
      <div className="p-5">
        <LoadingSpinner size={5} />
      </div>
    );
  }

  if (!promptRegistry || !sourceVersion || !targetVersion) {
    return (
      <NotFoundMessage
        title="Template not found"
        subtitle="We couldn't find the prompt template you were looking for."
        backLink="/prompt"
        backLinkMessage="Back to Prompt Registry"
      />
    );
  }

  return (
    <BreadcrumbsPageWrapper {...breadcrumbProps} className="pb-3">
      <div className="flex w-full flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to={`/workspace/${userContext?.activeWorkspaceId}/prompt/${promptId}/version/${versionNumber}`}
              className="text-sm font-semibold hover:text-blue-600"
            >
              Version {versionNumber}
            </Link>
            <span className="text-sm text-gray-400">vs</span>
            <Link
              to={`/workspace/${userContext?.activeWorkspaceId}/prompt/${promptId}/version/${diffVersionNumber}`}
              className="text-sm font-semibold hover:text-blue-600"
            >
              Version {diffVersionNumber}
            </Link>
            {isXrayMode && (
              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                X-Ray
              </span>
            )}
          </div>
          <DiffToolbar
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            isXrayMode={isXrayMode}
            onToggleXray={() => setIsXrayMode((prev: boolean) => !prev)}
            isXrayAvailable={isXrayAvailable}
          />
        </div>

        <MetadataDiff sourceVersion={sourceVersion} targetVersion={targetVersion} />

        <div className="w-full rounded-md border border-gray-200">
          {viewMode === "readable" && sourceTemplate && targetTemplate ? (
            <MessageDiff sourceTemplate={sourceTemplate} targetTemplate={targetTemplate} />
          ) : (
            <div className="overflow-x-auto">
              <ReactDiffViewer
                oldValue={formatPromptVersionForJsonDiff(sourceVersion)}
                newValue={formatPromptVersionForJsonDiff(targetVersion)}
                splitView
                hideLineNumbers
                showDiffOnly
                extraLinesSurroundingDiff={2}
                compareMethod={DiffMethod.WORDS}
              />
            </div>
          )}
        </div>
      </div>
    </BreadcrumbsPageWrapper>
  );
};

export default DiffPromptTemplate;
